import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import useAutoRefresh from '@/hooks/useAutoRefresh';
import {
  Activity, RefreshCw, Filter, User, Calendar, FileText,
  CheckCircle, AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_COLORS = {
  bulk_upload: 'bg-emerald-100 text-emerald-700',
  export: 'bg-purple-100 text-purple-700',
  toggle_active: 'bg-amber-100 text-amber-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
};

const colorFor = (action) => {
  const key = (action || '').split('.').pop();
  return ACTION_COLORS[key] || 'bg-slate-100 text-slate-700';
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(ts).slice(0, 19);
  }
};

const DetailsBadge = ({ details }) => {
  if (!details || Object.keys(details).length === 0) return <span className="text-slate-400 text-xs">—</span>;
  const keys = Object.keys(details);
  return (
    <div className="flex flex-wrap gap-1.5 text-xs">
      {keys.slice(0, 4).map((k) => {
        const v = details[k];
        const value = typeof v === 'object' ? JSON.stringify(v) : String(v);
        return (
          <span key={k} className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-700">
            <strong className="mr-1">{k}:</strong>
            <span className="font-mono truncate max-w-[140px]" title={value}>{value}</span>
          </span>
        );
      })}
      {keys.length > 4 && <span className="text-slate-400">+{keys.length - 4} more</span>}
    </div>
  );
};

const AdminActivityLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [limit, setLimit] = useState(100);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const params = new URLSearchParams();
      params.set('limit', limit);
      if (actionFilter) params.set('action', actionFilter);
      if (resourceFilter) params.set('resource', resourceFilter);
      const res = await axios.get(`${API}/admin/activity-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to load activity logs', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [actionFilter, resourceFilter, limit]);

  useAutoRefresh(() => fetchLogs());

  const actionOptions = useMemo(() => [
    { v: '', label: 'All actions' },
    { v: 'course', label: 'Course (any)' },
    { v: 'course.bulk_upload', label: 'Course Bulk Upload' },
    { v: 'course.export', label: 'Course Export' },
    { v: 'course.toggle_active', label: 'Course Toggle' },
    { v: 'course.update', label: 'Course Update' },
    { v: 'course.delete', label: 'Course Delete' },
    { v: 'question_bank', label: 'Question Bank (any)' },
    { v: 'question_bank.bulk_upload', label: 'Question Bank Upload' },
    { v: 'question_bank.export', label: 'Question Bank Export' },
  ], []);

  return (
    <div className="space-y-6" data-testid="admin-activity-logs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-600" /> Admin Activity Log
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Who did what, when. Compliance & accountability — never forgets.
          </p>
        </div>
        <button
          onClick={fetchLogs}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          data-testid="refresh-activity-logs"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-slate-400" />
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="filter-action"
        >
          {actionOptions.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <select
          value={resourceFilter}
          onChange={(e) => setResourceFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="filter-resource"
        >
          <option value="">All resources</option>
          <option value="course">Course</option>
          <option value="question">Question</option>
        </select>
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="filter-limit"
        >
          <option value={50}>Last 50</option>
          <option value={100}>Last 100</option>
          <option value={250}>Last 250</option>
          <option value={500}>Last 500</option>
        </select>
        <span className="ml-auto text-xs text-slate-500">
          Showing <strong className="text-slate-900">{logs.length}</strong> of <strong className="text-slate-900">{total}</strong>
        </span>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading activity log…</div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-slate-500 flex flex-col items-center gap-2">
            <AlertCircle className="w-6 h-6 text-slate-400" /> No activity yet for this filter.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600 w-44"><Calendar className="inline w-3.5 h-3.5 mr-1" /> When</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600"><User className="inline w-3.5 h-3.5 mr-1" /> Admin</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Action</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Resource</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600"><FileText className="inline w-3.5 h-3.5 mr-1" /> Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50" data-testid={`activity-row-${l.id}`}>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(l.timestamp)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{l.admin_email || '—'}</div>
                    <div className="text-xs text-slate-400 capitalize">{l.admin_role || ''}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorFor(l.action)}`}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {l.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {l.resource || '—'}
                    {l.resource_id && (
                      <div className="text-xs text-slate-400 font-mono truncate max-w-[200px]" title={l.resource_id}>
                        {l.resource_id.slice(0, 12)}…
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3"><DetailsBadge details={l.details} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminActivityLogsPage;
